import { Dialog, DialogUtility, createSpinner, hideSpinner, showSpinner } from '@syncfusion/ej2-react-popups'
import CustomCard from '../../../components/CustomCard.tsx'
import { ColumnDirective, ColumnsDirective, CommandClickEventArgs, CommandColumn, CommandModel, GridComponent, Inject } from '@syncfusion/ej2-react-grids'
import { useEffect, useState } from 'react'
import { protectedResources } from '../../../global/config.tsx'
import { useOidcAccessToken } from '@axa-fr/react-oidc'

import { fetchDataFromApi } from '../../../services/fetchDataFromApi.tsx'
import { DocumentUpload } from './DocumentUpload.tsx'
import { getValue } from '@syncfusion/ej2-base'

import { useAppDispatch, useAppSelector } from '../../../hooks/reduxHooks.tsx'
import { fetchFileTypesFromApiData } from '../../../redux/settingSlice.tsx'
import { deleteFile, removeFile } from '../../../redux/registerSlice.tsx'
import { NwDbFile } from '../../../types/settingTypes/NwDbFile.tsx'
import { NwFileType } from '../../../types/settingTypes/NwFileType.tsx'

export const DocumentsData = () => {
  const { accessToken } = useOidcAccessToken()
  const [spinnerTarget, setSpinnerTarget] = useState<HTMLElement>()
  const [docUploadVisible, setDocUploadVisible] = useState<boolean>(false)
  const fileTypes: NwFileType[] | undefined = useAppSelector((state) => state.setting.fileTypeList)
  const files: NwDbFile[] | undefined = useAppSelector((state) => state.register.files)
  const [isFileTypesLoading, setIsFileTypesLoading] = useState<boolean>(false)

  let deleteConfirmInstance: Dialog | null
  let gridInstance: GridComponent | null

  const dispatch = useAppDispatch()

  // Hole File Types aus API
  useEffect(() => {
    const fileTypesUrl = `${protectedResources.apiList.readFileTypes}`

    if (accessToken) {
      setIsFileTypesLoading(true)

      dispatch(fetchFileTypesFromApiData({ url: fileTypesUrl, token: accessToken }))
        .then(() => {
          setIsFileTypesLoading(false)
        })
        .catch(() => {
          setIsFileTypesLoading(false)
        })
    }
  }, [accessToken])

  useEffect(() => {
    setSpinnerTarget(document.getElementsByClassName('nw-app')[0] as HTMLElement)

    if (spinnerTarget instanceof HTMLElement) {
      createSpinner({
        target: spinnerTarget,
      })
    }
  }, [])

  const commands: CommandModel[] = [
    {
      title: 'Download',
      buttonOption: { cssClass: 'e-flat btnEdit', iconCss: 'e-icons e-download-2' },
    },
    {
      title: 'Löschen',
      buttonOption: { cssClass: 'e-flat btnDelete', iconCss: 'e-icons e-delete-1' },
    },
  ]

  const clickGrid = (props: CommandClickEventArgs) => {
    let selectedFile = props.rowData as NwDbFile

    switch (props.commandColumn?.title) {
      case 'Download':
        setTimeout(() => {
          gridInstance?.clearSelection()
          getFile(selectedFile)
        })
        break
      case 'Löschen':
        setTimeout(() => {
          gridInstance?.clearSelection()

          const deleteFileurl = `${protectedResources.apiList.deleteFile}/${selectedFile.id}`
          deleteConfirmInstance = DialogUtility.confirm({
            title: 'Löschen bestätigen',
            width: '550px',
            content: `<strong>Möchten Sie die Datei "${selectedFile.fileName}" wirklich löschen?</strong>`,
            position: { X: 'center', Y: 'center' },
            okButton: {
              text: 'Löschen',
              click: () => {
                if (accessToken) {
                  if (spinnerTarget) showSpinner(spinnerTarget)

                  dispatch(deleteFile({ url: deleteFileurl, token: accessToken, bodyData: selectedFile }))
                    .then(() => {
                      dispatch(removeFile(selectedFile))
                    })
                    .finally(() => {
                      deleteConfirmInstance?.hide()
                      if (spinnerTarget) hideSpinner(spinnerTarget)
                    })
                }
              },
            },
            cancelButton: {
              text: 'Abbrechen',
              click: () => {
                deleteConfirmInstance?.hide()
              },
            },
          })
        })
        break
    }
  }

  async function getFile(file: NwDbFile) {
    if (file) {
      const readFileContentUrl = `${protectedResources.apiList.readFileContent}/${file.id}`
      if (spinnerTarget) showSpinner(spinnerTarget)
      await fetchAndDownload(readFileContentUrl, file.fileName)
      if (spinnerTarget) hideSpinner(spinnerTarget)
    }
  }

  async function fetchAndDownload(apiUrl: string, fileName: string) {
    try {
      const response = await fetchDataFromApi(apiUrl, accessToken, 'GET', 'blob')
      const href = URL.createObjectURL(response.data)
      const link = document.createElement('a')
      link.href = href
      link.setAttribute('download', fileName)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(href)
    } catch (error) {
      // Fehlerbehandlung hier
      console.error('Fehler beim Herunterladen der Datei:', error)
    }
  }

  const showDocUpload = () => {
    setDocUploadVisible(true)
  }

  // @ts-ignore: zeige Dok. Art Text an, anstell von ID
  const concatenateFields = (field: string, data: object) => {
    const fileTypeId = getValue('fileTypId', data)
    const fileType = fileTypes?.find((type) => type.id === fileTypeId)
    return fileType ? fileType.fileType : '-'
  }

  return (
    <>
      {docUploadVisible && <DocumentUpload />}

      {!isFileTypesLoading && (
        <CustomCard header={`Dokumente`} headerBtnLabel="Neu" headerBtnAction={showDocUpload} headerBtnIcon="e-plus">
          <GridComponent dataSource={files} commandClick={clickGrid} ref={(grid) => (gridInstance = grid)}>
            <ColumnsDirective>
              <ColumnDirective headerText="#" allowFiltering={false} textAlign="Center" commands={commands} minWidth={'10%'} width={'10%'} />
              <ColumnDirective field="fileName" headerText="Dateiname" />
              <ColumnDirective field="fileTypId" headerText="Dokumentenart" valueAccessor={concatenateFields} />
            </ColumnsDirective>
            <Inject services={[CommandColumn]} />
          </GridComponent>
        </CustomCard>
      )}
    </>
  )
  // return (<></>)
}
